import React, { useState, useMemo } from 'react';
import { Box, Tooltip, Chip } from '@mui/material';
import { Gif as GifIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// Styled image with GIF optimizations
const StyledImage = styled('img')(({ theme, isGif }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 'inherit',
  transition: 'transform 0.3s ease',
  
  // GIF-specific optimizations
  ...(isGif && {
    imageRendering: 'auto', // Better GIF rendering
    '&:hover': {
      transform: 'scale(1.02)', // Subtle zoom on hover for GIFs
    },
  }),
}));

const GifAwareImage = ({
  src,
  alt,
  sx = {},
  isGif = null, // Can be explicitly set, otherwise auto-detected
  showGifIndicator = true, // Show GIF indicator chip
  gifIndicatorPosition = 'top-right', // Position of GIF indicator
  onError,
  onLoad,
  ...props
}) => {
  const { t } = useTranslation(['common']);
  const [hasError, setHasError] = useState(false);
  
  // Auto-detect if image is GIF
  const detectedIsGif = useMemo(() => {
    if (isGif !== null) return isGif;
    return src && src.toLowerCase().endsWith('.gif');
  }, [src, isGif]);

  // Handle image load errors
  const handleError = (e) => {
    setHasError(true);
    if (onError) onError(e);
  };

  // Handle image load success
  const handleLoad = (e) => {
    setHasError(false);
    if (onLoad) onLoad(e);
  };

  // Get GIF indicator position styles
  const getGifIndicatorPosition = () => {
    switch (gifIndicatorPosition) {
      case 'top-left':
        return { top: 8, left: 8 };
      case 'top-right':
        return { top: 8, right: 8 };
      case 'bottom-left':
        return { bottom: 8, left: 8 };
      case 'bottom-right':
        return { bottom: 8, right: 8 };
      default:
        return { top: 8, right: 8 };
    }
  };

  if (hasError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.200',
          borderRadius: 'inherit',
          ...sx,
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
          <div>⚠️</div>
          <div style={{ fontSize: '0.75rem' }}>Image failed to load</div>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <StyledImage
        src={src}
        alt={alt}
        isGif={detectedIsGif}
        onError={handleError}
        onLoad={handleLoad}
        {...props}
      />
      
      {/* GIF Indicator */}
      {detectedIsGif && showGifIndicator && (
        <Tooltip title={t('common:gif.animated')} arrow>
          <Chip
            icon={<GifIcon />}
            label={t('common:gif.indicator')}
            size="small"
            sx={{
              position: 'absolute',
              ...getGifIndicatorPosition(),
              zIndex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontSize: '0.7rem',
              height: '20px',
              '& .MuiChip-icon': {
                fontSize: '0.8rem',
                color: 'white',
              },
              '& .MuiChip-label': {
                fontSize: '0.7rem',
                fontWeight: 'bold',
              },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default GifAwareImage;
